import React, {createContext, useContext, useEffect, useRef} from 'react';
import config from 'utils/config';
import pollActionForUserId from './pollActionForUserId';
import withAuth from '../auth/withAuth';
import StoreActions from '../../../actions/storeActions';

const ServerEventsContext = createContext({});

const MESSAGES_EVENT = 'messages';

let subscribers = {};

const addServerEventListener = (eventName, callback) => {
	if (typeof callback !== 'function') {
		throw new Error('ServerEventsProvider: callback must be a function');
	}

	if (subscribers[eventName]) {
		throw new Error(
			'ServerEventsProvider: callback already exist for a given event'
		);
	}

	subscribers[eventName] = callback;
};

const removeServerEventListener = eventName => {
	if (subscribers[eventName]) {
		delete subscribers[eventName];
	}
};

const removeAllServerEventListeners = () => {
	subscribers = {};
};

const onNewMessage = ({data}) => {
	StoreActions.appendMessage(data.message);
};

const Provider = ({children, authUser}) => {
	const isPollingRef = useRef(false);

	useEffect(() => {
		async function listenToServerEvents() {
			// TODO: only poll if there are subscribers
			const shouldNotPoll =
				!authUser ||
				!authUser.id ||
				isPollingRef.current ||
				!config.serverEventsEnabled ||
				!document.hasFocus();

			if (shouldNotPoll) {
				return;
			}

			try {
				isPollingRef.current = true;

				const event = await pollActionForUserId(authUser.id);

				// FYI: events has to have shape of { type: 'something', data: { /* your data */ } }
				if (event.type && event.data && subscribers[event.type]) {
					subscribers[event.type](event);
				} else if (event.type === MESSAGES_EVENT && event.data) {
					onNewMessage(event);
				}

				isPollingRef.current = false;
				listenToServerEvents();
			} catch (err) {
				// polling only happens for authenticated users
				if (err.status === 401) {
					return;
				}

				console.error('ServerEventsProvider: poll action error', err); // eslint-disable-line no-console

				isPollingRef.current = false;
				// TODO: check whether we need to stop polling after specific number of retries
				setTimeout(() => {
					listenToServerEvents();
				}, 30_000);
			}
		}

		listenToServerEvents();

		window.addEventListener('focus', listenToServerEvents);
		window.addEventListener('blur', listenToServerEvents);

		return () => {
			isPollingRef.current = false;
			window.removeEventListener('focus', listenToServerEvents);
			window.removeEventListener('blur', listenToServerEvents);
		};
	}, [authUser]);

	return (
		<ServerEventsContext.Provider
			value={{
				addServerEventListener,
				removeServerEventListener,
				removeAllServerEventListeners,
			}}
		>
			{children}
		</ServerEventsContext.Provider>
	);
};

const ServerEventsProvider = withAuth(Provider);

const useServerEvents = () => useContext(ServerEventsContext);

export {ServerEventsProvider, ServerEventsContext, useServerEvents};
